import React, { useCallback } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { Modal, Form, notification, Spin, Row } from 'antd'
import { map, filter } from 'lodash-es'
import { UPDATE_SCRAPING_TEST_CASE_TAGS } from '../../../graphql/mutations'
import {
  SCRAPING_TAGS_FOR_TEST_CASE,
  SCRAPING_TAGS_FOR_TYPE,
} from '../../../graphql/queries'
import { transformToUpdateScrapingTestCaseTags } from '../../../graphql/transformers'
import { CUSTOM } from '../../../constants'
import { ScrapingTestCaseTagsForm } from '../../forms'
import PropTypes from 'prop-types'

const ScrapingTestCaseTagsModal = ({
  testCaseId,
  refetchScrapingTestCases,
  isModalOpen,
  closeModal,
}) => {
  const {
    data: { scrapingTagsForType } = {},
    loading: isLoadingTagsForType,
  } = useQuery(SCRAPING_TAGS_FOR_TYPE, {
    variables: { type: CUSTOM },
    fetchPolicy: 'no-cache',
  })

  const {
    data: { scrapingTagsForTestCaseId = [] } = {},
    loading: isLoadingTagsForTestCase,
  } = useQuery(SCRAPING_TAGS_FOR_TEST_CASE, {
    variables: {
      testCaseId,
    },
    skip: !testCaseId,
    fetchPolicy: 'no-cache',
  })

  const [
    updateScrapingTestCaseTags,
    { loading: isUpdatingScrapingTestCaseTags },
  ] = useMutation(UPDATE_SCRAPING_TEST_CASE_TAGS, {
    onCompleted() {
      refetchScrapingTestCases()
      closeModal()
      notification.open({
        message: 'Test case tags were updated.',
        type: 'success',
      })
    },
    onError({ message }) {
      notification.open({
        message: 'Faled to update test case tags.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const [formRef] = Form.useForm()

  const handleSubmit = useCallback(
    ({ customTagIds }) => {
      const previousCustomTagIds = map(
        filter(scrapingTagsForTestCaseId, ({ type }) => type === CUSTOM),
        'id',
      )

      const variables = transformToUpdateScrapingTestCaseTags(
        testCaseId,
        customTagIds,
        previousCustomTagIds,
      )

      updateScrapingTestCaseTags({ variables })
    },
    [testCaseId, scrapingTagsForTestCaseId, updateScrapingTestCaseTags],
    [],
  )

  return (
    <Modal
      title="Configure tags"
      visible={isModalOpen}
      onOk={() => formRef.submit()}
      onCancel={closeModal}
      confirmLoading={isUpdatingScrapingTestCaseTags}
    >
      {isLoadingTagsForType || isLoadingTagsForTestCase ? (
        <Row justify="center">
          <Spin tip="Loading..." />
        </Row>
      ) : (
        <ScrapingTestCaseTagsForm
          tags={scrapingTagsForType}
          tagsForTestCase={scrapingTagsForTestCaseId}
          onSubmit={handleSubmit}
          formRef={formRef}
        />
      )}
    </Modal>
  )
}

ScrapingTestCaseTagsModal.propTypes = {
  testCaseId: PropTypes.string,
  refetchScrapingTestCases: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
}

ScrapingTestCaseTagsModal.defaultProps = {
  testCaseId: undefined,
}

export default ScrapingTestCaseTagsModal
